/**
 * Admin Management Dialogs for Master Database
 * - Bug Reports Management
 * - Known Issues Management
 */
import { useState } from "react";
import { Bug, AlertCircle, Edit2, Trash2, Check, X, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Bug Reports Management Dialog
 */
export function BugReportsDialog({
  open,
  onOpenChange,
  bugReports,
  bugReportsLoading,
  onUpdateReport,
  onDeleteReport,
}) {
  const [editingReport, setEditingReport] = useState(null);

  const handleUpdate = async (reportId, data) => {
    await onUpdateReport(reportId, data);
    setEditingReport(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="w-5 h-5 text-amber-500" />
            Bug Reports Management
          </DialogTitle>
          <DialogDescription className="text-white/60">
            View and manage user-submitted bug reports
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {bugReportsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-white/50" />
            </div>
          ) : !bugReports || bugReports.length === 0 ? (
            <p className="text-center text-white/50 py-8">No bug reports submitted yet</p>
          ) : (
            <div className="space-y-3">
              {bugReports?.map((report) => (
                <BugReportCard
                  key={report.id}
                  report={report}
                  isEditing={editingReport?.id === report.id}
                  editingData={editingReport}
                  onStartEdit={() => setEditingReport({...report})}
                  onCancelEdit={() => setEditingReport(null)}
                  onUpdate={(data) => handleUpdate(report.id, data)}
                  onDelete={() => onDeleteReport(report.id)}
                  onEditingDataChange={setEditingReport}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BugReportCard({
  report,
  isEditing,
  editingData,
  onStartEdit,
  onCancelEdit,
  onUpdate,
  onDelete,
  onEditingDataChange,
}) {
  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-white">{report.title}</h4>
            <span className={`text-xs px-2 py-0.5 rounded ${
              report.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
              report.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
              report.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-blue-500/20 text-blue-400'
            }`}>
              {report.severity}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded ${
              report.status === 'open' ? 'bg-green-500/20 text-green-400' :
              report.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
              report.status === 'resolved' ? 'bg-purple-500/20 text-purple-400' :
              report.status === 'wont_fix' ? 'bg-gray-500/20 text-gray-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {report.status?.replace('_', ' ')}
            </span>
            <span className="text-xs text-white/40">{report.category}</span>
          </div>
          <p className="text-sm text-white/70 mt-2">{report.description}</p>
          {report.stepsToReproduce && (
            <div className="mt-2">
              <span className="text-xs text-white/40">Steps to reproduce:</span>
              <p className="text-xs text-white/60">{report.stepsToReproduce}</p>
            </div>
          )}
          <p className="text-xs text-white/40 mt-2">
            Submitted: {new Date(report.createdAt).toLocaleDateString()}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {isEditing ? (
            <>
              <Select 
                value={editingData?.status || 'open'} 
                onValueChange={(v) => onEditingDataChange({...editingData, status: v})}
              >
                <SelectTrigger className="w-32 h-8 text-xs bg-black/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="wont_fix">Won't Fix</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => onUpdate({ status: editingData.status })}
                className="h-8 bg-green-600 hover:bg-green-500"
              >
                <Check className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onCancelEdit}
                className="h-8 border-white/20"
              >
                <X className="w-3 h-3" />
              </Button>
            </>
          ) : (
            <>
              {report.category === "cross_play_link" && report.status !== "resolved" && report.crossPlaySuggestion && (
                <Button
                  size="sm"
                  data-testid={`bug-apply-crossplay-${report.id}`}
                  onClick={async () => {
                    try {
                      const axios = (await import("@/lib/api")).default;
                      const { getApiUrl } = await import("@/lib/api");
                      await axios.post(`${getApiUrl()}/api/multiplayer/admin/cross-play-suggest/${report.id}/apply`);
                      const { toast } = await import("sonner");
                      toast.success("Cross-play link applied");
                      onUpdate({ status: "resolved" });
                    } catch (err) {
                      const { toast } = await import("sonner");
                      toast.error(err.response?.data?.detail || "Failed to apply");
                    }
                  }}
                  className="h-8 bg-violet-600 hover:bg-violet-500"
                  title="Apply this cross-play link suggestion"
                >
                  Apply Link
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={onStartEdit}
                className="h-8 border-white/20 text-white/70"
              >
                <Edit2 className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onDelete}
                className="h-8 border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Known Issues Management Dialog
 */
export function KnownIssuesDialog({
  open,
  onOpenChange,
  knownIssues,
  knownIssuesLoading,
  onCreateIssue,
  onUpdateIssue,
  onDeleteIssue,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [newIssue, setNewIssue] = useState({
    title: "",
    description: "",
    status: "investigating",
    workaround: "",
  });

  const handleCreate = async () => {
    if (!newIssue.title.trim() || !newIssue.description.trim()) return;
    await onCreateIssue(newIssue);
    setNewIssue({ title: "", description: "", status: "investigating", workaround: "" });
    setIsCreating(false);
  };

  const handleUpdate = async (issueId, data) => {
    await onUpdateIssue(issueId, data);
    setEditingIssue(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Known Issues Manager
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Manage known issues displayed on the Bug Reports page
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          {/* Create New Issue Form */}
          {isCreating ? (
            <div className="p-4 bg-white/5 rounded-lg border border-purple-500/30 space-y-3">
              <h4 className="font-medium text-purple-400">New Known Issue</h4>
              <div className="space-y-2">
                <Label className="text-xs">Title</Label>
                <Input
                  value={newIssue.title}
                  onChange={(e) => setNewIssue({...newIssue, title: e.target.value})}
                  className="bg-black/50 border-white/10"
                  placeholder="Brief issue title..."
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={newIssue.description}
                  onChange={(e) => setNewIssue({...newIssue, description: e.target.value})}
                  className="bg-black/50 border-white/10 min-h-[80px]"
                  placeholder="Detailed description of the issue..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Status</Label>
                  <Select value={newIssue.status} onValueChange={(v) => setNewIssue({...newIssue, status: v})}>
                    <SelectTrigger className="bg-black/50 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="investigating">Investigating</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="fixed">Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Workaround (optional)</Label>
                  <Input
                    value={newIssue.workaround}
                    onChange={(e) => setNewIssue({...newIssue, workaround: e.target.value})}
                    className="bg-black/50 border-white/10"
                    placeholder="Any known workaround..."
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreate} className="bg-purple-600 hover:bg-purple-500">
                  Create Issue
                </Button>
                <Button variant="outline" onClick={() => setIsCreating(false)} className="border-white/20">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setIsCreating(true)} className="bg-purple-600 hover:bg-purple-500">
              <Plus className="w-4 h-4 mr-2" />
              Add Known Issue
            </Button>
          )}
          
          {/* Issues List */}
          {knownIssuesLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-white/50" />
            </div>
          ) : !knownIssues || knownIssues.length === 0 ? (
            <p className="text-center text-white/50 py-8">No known issues added yet</p>
          ) : (
            <div className="space-y-3">
              {knownIssues?.map((issue) => (
                <KnownIssueCard
                  key={issue.id}
                  issue={issue}
                  isEditing={editingIssue?.id === issue.id}
                  editingData={editingIssue}
                  onStartEdit={() => setEditingIssue({...issue})}
                  onCancelEdit={() => setEditingIssue(null)}
                  onUpdate={(data) => handleUpdate(issue.id, data)}
                  onDelete={() => onDeleteIssue(issue.id)}
                  onEditingDataChange={setEditingIssue}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KnownIssueCard({
  issue,
  isEditing,
  editingData,
  onStartEdit,
  onCancelEdit,
  onUpdate,
  onDelete,
  onEditingDataChange,
}) {
  if (isEditing) {
    return (
      <div className="p-4 bg-white/5 rounded-lg border border-blue-500/30 space-y-3">
        <div className="space-y-2">
          <Label className="text-xs">Title</Label>
          <Input
            value={editingData.title}
            onChange={(e) => onEditingDataChange({...editingData, title: e.target.value})}
            className="bg-black/50 border-white/10"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Description</Label>
          <Textarea
            value={editingData.description}
            onChange={(e) => onEditingDataChange({...editingData, description: e.target.value})}
            className="bg-black/50 border-white/10 min-h-[80px]"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs">Status</Label>
            <Select 
              value={editingData.status} 
              onValueChange={(v) => onEditingDataChange({...editingData, status: v})}
            >
              <SelectTrigger className="bg-black/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="investigating">Investigating</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Workaround</Label>
            <Input
              value={editingData.workaround || ""}
              onChange={(e) => onEditingDataChange({...editingData, workaround: e.target.value})}
              className="bg-black/50 border-white/10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => onUpdate({
              title: editingData.title,
              description: editingData.description,
              status: editingData.status,
              workaround: editingData.workaround,
            })} 
            className="bg-green-600 hover:bg-green-500"
          >
            <Check className="w-4 h-4 mr-2" />
            Save
          </Button>
          <Button variant="outline" onClick={onCancelEdit} className="border-white/20">
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-white">{issue.title}</h4>
            <span className={`text-xs px-2 py-0.5 rounded ${
              issue.status === 'investigating' ? 'bg-yellow-500/20 text-yellow-400' :
              issue.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
              issue.status === 'fixed' ? 'bg-green-500/20 text-green-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>
              {issue.status?.replace('_', ' ')}
            </span>
          </div>
          <p className="text-sm text-white/70 mt-1">{issue.description}</p>
          {issue.workaround && (
            <p className="text-xs text-white/50 mt-2">
              <span className="text-amber-400">Workaround:</span> {issue.workaround}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onStartEdit}
            className="h-8 border-white/20 text-white/70"
          >
            <Edit2 className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="h-8 border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
